import { useRef, useState } from 'react';
import { formatWhen } from '../../utils/dateUtils';
import { errorMessage, WAIVER_LABELS } from './helpers';
import {
  ACCEPT, ALLOWED_DESCRIPTION, MAX_RECEIPTS, canPreview, fmtBytes, openReceipt, uploadReceipt, validateReceiptFile,
} from './receiptUpload';

/*
 * Receipt state, held by the parent so it can validate and submit it:
 *   uploads: files chosen here, each { key, file, name, size, status, progress, uploadId, error }
 *            status: 'uploading' | 'done' | 'error'
 *   remove:  receipt_ids of current receipts to remove
 *   waiver:  { checked, reason, explanation } -- "I don't have a receipt"
 */

/** Starting state for a new expense (no expense) or an existing one. */
export const initialReceiptState = (expense, current = []) => ({
  uploads: [],
  remove: [],
  waiver: {
    checked: Boolean(expense?.receipt_waiver) && current.length === 0,
    reason: expense?.receipt_waiver?.reason || '',
    explanation: expense?.receipt_waiver?.explanation || '',
  },
});

/** How many receipts the expense will have once this state is saved. */
export const remainingCount = (state, current) =>
  current.filter((r) => !state.remove.includes(r.receipt_id)).length +
  state.uploads.filter((u) => u.status !== 'error').length;

/** Why this state can't be saved yet, or null. */
export const receiptProblem = (state, current) => {
  if (state.uploads.some((u) => u.status === 'uploading')) return 'Wait for the receipts to finish uploading.';
  if (state.uploads.some((u) => u.status === 'error')) return 'Remove or retry the receipts that failed to upload.';
  if (remainingCount(state, current) > 0) return null;
  if (!state.waiver.checked) return "Attach a receipt, or tick “I don't have a receipt to attach” and say why.";
  if (!state.waiver.reason) return 'Choose why there is no receipt.';
  if (!state.waiver.explanation.trim()) return 'Explain why there is no receipt.';
  return null;
};

const waiverOf = (state) => ({ reason: state.waiver.reason, explanation: state.waiver.explanation.trim() });

/** The receipt fields for submitting a new expense. */
export const createReceiptFields = (state) => {
  const ids = state.uploads.filter((u) => u.status === 'done').map((u) => u.uploadId);
  return ids.length ? { receipt_upload_ids: ids } : { receipt_waiver: waiverOf(state) };
};

/** The change to send for an existing expense, or null when nothing changed. */
export const receiptChangeFor = (state, current, expense) => {
  const add = state.uploads.filter((u) => u.status === 'done').map((u) => u.uploadId);
  const change = { add, remove: [...state.remove] };
  if (remainingCount(state, current) === 0) {
    const waiver = waiverOf(state);
    const existing = expense?.receipt_waiver;
    const same = existing && existing.reason === waiver.reason && existing.explanation === waiver.explanation;
    if (!same || change.remove.length) change.waiver = waiver;
  }
  return change.add.length || change.remove.length || change.waiver ? change : null;
};

let nextKey = 0;

const ReceiptRow = ({ receipt, onView, onDownload, children, struck }) => (
  <li className="d-flex flex-wrap align-items-center gap-2 py-1 border-bottom">
    <i className="bi bi-file-earmark-text" aria-hidden="true"></i>
    <span className={struck ? 'text-decoration-line-through text-muted' : ''}>{receipt.filename}</span>
    <span className="text-muted small">{fmtBytes(receipt.size)}</span>
    <span className="ms-auto d-flex gap-1">
      {canPreview(receipt.content_type) && (
        <button type="button" className="btn btn-link btn-sm py-0" onClick={onView} aria-label={`View ${receipt.filename}`}>
          View
        </button>
      )}
      <button type="button" className="btn btn-link btn-sm py-0" onClick={onDownload} aria-label={`Download ${receipt.filename}`}>
        Download
      </button>
      {children}
    </span>
  </li>
);

/**
 * The receipts part of the expense form and of the Receipts dialog: current
 * receipts (view, download, remove), new uploads with progress, the "no
 * receipt" reason, and -- for approvers -- earlier receipts.
 *
 * getLink(receipt, disposition) resolves to { url } for viewing or downloading.
 */
const ReceiptPicker = ({ current = [], earlier = [], state, setState, getLink, readOnly = false, idPrefix = 'receipts' }) => {
  const input = useRef(null);
  const [notice, setNotice] = useState('');
  const remaining = remainingCount(state, current);
  const full = remaining >= MAX_RECEIPTS;
  const waiverAvailable = remaining === 0;

  const patchUpload = (key, patch) =>
    setState((prev) => ({ ...prev, uploads: prev.uploads.map((u) => (u.key === key ? { ...u, ...patch } : u)) }));

  const start = (entry) => {
    uploadReceipt(entry.file, (progress) => patchUpload(entry.key, { progress }))
      .then((uploadId) => patchUpload(entry.key, { status: 'done', uploadId, progress: 1 }))
      .catch((err) => patchUpload(entry.key, { status: 'error', error: `Upload failed: ${errorMessage(err)}` }));
  };

  const addFiles = (fileList) => {
    const files = [...(fileList || [])];
    const room = MAX_RECEIPTS - remaining;
    setNotice(files.length > room ? `An expense can have up to ${MAX_RECEIPTS} receipts; ${files.length - room} not added.` : '');
    const entries = files.slice(0, Math.max(room, 0)).map((file) => {
      nextKey += 1;
      const error = validateReceiptFile(file);
      return { key: `upload-${nextKey}`, file, name: file.name, size: file.size,
        status: error ? 'error' : 'uploading', progress: 0, uploadId: null, error, invalid: Boolean(error) };
    });
    // Choosing a file replaces "no receipt".
    setState((prev) => ({ ...prev, uploads: [...prev.uploads, ...entries], waiver: { ...prev.waiver, checked: false } }));
    entries.filter((e) => !e.error).forEach(start);
    if (input.current) input.current.value = '';
  };

  const retry = (entry) => {
    patchUpload(entry.key, { status: 'uploading', progress: 0, error: null });
    start(entry);
  };

  const dropUpload = (key) => setState((prev) => ({ ...prev, uploads: prev.uploads.filter((u) => u.key !== key) }));

  const toggleRemove = (receiptId) =>
    setState((prev) => ({
      ...prev,
      remove: prev.remove.includes(receiptId) ? prev.remove.filter((id) => id !== receiptId) : [...prev.remove, receiptId],
    }));

  const setWaiver = (patch) => setState((prev) => ({ ...prev, waiver: { ...prev.waiver, ...patch } }));

  const open = (receipt, disposition) => {
    setNotice('');
    openReceipt(() => getLink(receipt, disposition)).catch((err) => setNotice(`Couldn't open ${receipt.filename}: ${errorMessage(err)}`));
  };

  return (
    <div>
      {(current.length > 0 || state.uploads.length > 0) && (
        <ul className="list-unstyled mb-2">
          {current.map((receipt) => {
            const removing = state.remove.includes(receipt.receipt_id);
            return (
              <ReceiptRow
                key={receipt.receipt_id}
                receipt={receipt}
                struck={removing}
                onView={() => open(receipt, 'inline')}
                onDownload={() => open(receipt, 'attachment')}
              >
                {!readOnly && (
                  <button
                    type="button"
                    className={`btn btn-link btn-sm py-0 ${removing ? '' : 'text-danger'}`}
                    onClick={() => toggleRemove(receipt.receipt_id)}
                    aria-label={`${removing ? 'Keep' : 'Remove'} ${receipt.filename}`}
                  >
                    {removing ? 'Undo' : 'Remove'}
                  </button>
                )}
              </ReceiptRow>
            );
          })}
          {state.uploads.map((upload) => (
            <li key={upload.key} className="py-1 border-bottom" data-testid="receipt-upload">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <i className={`bi ${upload.status === 'error' ? 'bi-exclamation-triangle text-danger' : 'bi-file-earmark-arrow-up'}`} aria-hidden="true"></i>
                <span>{upload.name}</span>
                <span className="text-muted small">{fmtBytes(upload.size)}</span>
                <span className="ms-auto d-flex gap-1 align-items-center">
                  {upload.status === 'done' && <span className="text-success small"><i className="bi bi-check-circle me-1"></i>Uploaded</span>}
                  {upload.status === 'error' && !upload.invalid && (
                    <button type="button" className="btn btn-link btn-sm py-0" onClick={() => retry(upload)}>Retry</button>
                  )}
                  <button type="button" className="btn btn-link btn-sm py-0 text-danger" onClick={() => dropUpload(upload.key)} aria-label={`Remove ${upload.name}`}>
                    Remove
                  </button>
                </span>
              </div>
              {upload.status === 'uploading' && (
                <div className="progress mt-1" style={{ height: 4 }} role="progressbar" aria-label={`Uploading ${upload.name}`}
                  aria-valuenow={Math.round(upload.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="progress-bar" style={{ width: `${Math.round(upload.progress * 100)}%` }}></div>
                </div>
              )}
              {upload.error && <div className="small text-danger">{upload.error}</div>}
            </li>
          ))}
        </ul>
      )}

      {readOnly && current.length === 0 && <p className="text-muted small mb-2">No receipts attached.</p>}

      {!readOnly && (
        <>
          <input
            ref={input}
            id={`${idPrefix}Files`}
            type="file"
            className="form-control"
            multiple
            accept={ACCEPT}
            onChange={(e) => addFiles(e.target.files)}
            disabled={full}
            aria-label="Add receipt files"
          />
          <div className="form-text">
            {ALLOWED_DESCRIPTION}. Up to {MAX_RECEIPTS} per expense.
          </div>

          <div className="form-check mt-2">
            <input
              id={`${idPrefix}NoReceipt`}
              className="form-check-input"
              type="checkbox"
              checked={state.waiver.checked && waiverAvailable}
              disabled={!waiverAvailable}
              onChange={(e) => setWaiver({ checked: e.target.checked })}
            />
            <label className="form-check-label" htmlFor={`${idPrefix}NoReceipt`}>
              I don&apos;t have a receipt to attach
            </label>
            {!waiverAvailable && <div className="form-text">Remove the attached receipts to use this.</div>}
          </div>
          {state.waiver.checked && waiverAvailable && (
            <div className="row g-2 mt-1">
              <div className="col-sm-5">
                <label htmlFor={`${idPrefix}Reason`} className="form-label small mb-1">Reason *</label>
                <select
                  id={`${idPrefix}Reason`}
                  className="form-select form-select-sm"
                  value={state.waiver.reason}
                  onChange={(e) => setWaiver({ reason: e.target.value })}
                >
                  <option value="">Choose…</option>
                  {Object.entries(WAIVER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm-7">
                <label htmlFor={`${idPrefix}Explanation`} className="form-label small mb-1">Explanation *</label>
                <input
                  id={`${idPrefix}Explanation`}
                  type="text"
                  className="form-control form-control-sm"
                  maxLength={500}
                  placeholder="For example: paper receipt to follow"
                  value={state.waiver.explanation}
                  onChange={(e) => setWaiver({ explanation: e.target.value })}
                />
              </div>
            </div>
          )}
        </>
      )}

      {notice && <div className="small text-danger mt-2" role="alert">{notice}</div>}

      {earlier.length > 0 && (
        <details className="mt-3">
          <summary className="small">Earlier receipts ({earlier.length})</summary>
          <ul className="list-unstyled mb-0 mt-1">
            {earlier.map((receipt) => (
              <ReceiptRow
                key={receipt.receipt_id}
                receipt={receipt}
                onView={() => open(receipt, 'inline')}
                onDownload={() => open(receipt, 'attachment')}
              >
                <span className="text-muted small">
                  removed by {receipt.removed_by_name}, {formatWhen(receipt.removed_at)}
                </span>
              </ReceiptRow>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ReceiptPicker;
